import axios, { AxiosError, AxiosHeaders, InternalAxiosRequestConfig } from 'axios';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { clearClientSessionAndRedirectGuest } from '@/utils/auth-session-clear';

const ENV_API_URL = process.env.VUE_APP_API_URL;

/**
 * В development при ionic serve (другой порт, чем API) запросы идут на текущий origin — их проксирует vue.config.js.
 * В prod и на нативе остаётся VUE_APP_API_URL.
 */
function axiosBaseURL(): string | undefined {
  if (process.env.NODE_ENV !== 'development') {
    return ENV_API_URL;
  }
  if (!ENV_API_URL || typeof window === 'undefined' || Capacitor.isNativePlatform()) {
    return ENV_API_URL;
  }
  try {
    const normalized = ENV_API_URL.endsWith('/') ? ENV_API_URL : `${ENV_API_URL}/`;
    if (new URL(normalized).origin !== window.location.origin) {
      return '';
    }
  } catch {
    return ENV_API_URL;
  }
  return ENV_API_URL;
}

const SKIP_401_URLS = /authenticate|login|management\/info|refresh-token|api\/public\/showcase|public\/showcase/;

function requestTargetsPublicShowcase(config: InternalAxiosRequestConfig): boolean {
  const u = (config.url || '').toString();
  if (u.includes('public/showcase') || u.includes('api/public/showcase')) {
    return true;
  }
  const base = (config.baseURL || '').toString().replace(/\/$/, '');
  if (base && u) {
    try {
      const joined = new URL(u.replace(/^\//, ''), base.endsWith('/') ? base : `${base}/`).pathname;
      if (joined.includes('public/showcase')) return true;
    } catch {
      /* ignore */
    }
  }
  return false;
}

// Add a request interceptor
axios.interceptors.request.use(async function (config) {
  let token = localStorage.getItem('jhi-authenticationToken') || sessionStorage.getItem('jhi-authenticationToken');

  // На iOS/Android после входа токен часто только в Capacitor Preferences — подставляем и дублируем в localStorage для axios.
  if (!token?.trim() && Capacitor.isNativePlatform()) {
    try {
      const { value } = await Preferences.get({ key: 'jwt' });
      if (value?.trim()) {
        token = value;
        localStorage.setItem('jhi-authenticationToken', value);
        sessionStorage.removeItem('jhi-authenticationToken');
      }
    } catch {
      /* ignore */
    }
  }

  if (config.url) {
    config.baseURL = axiosBaseURL();
  }

  // Публичная витрина — без JWT (доступна гостю; снимаем Bearer, если axios подмешал defaults).
  if (requestTargetsPublicShowcase(config)) {
    config.headers = AxiosHeaders.from(config.headers ?? {});
    config.headers.delete('Authorization');
    config.headers.delete('authorization');
    return config;
  }

  if (config.headers && !config.headers.Authorization && config.url && token?.trim()) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
}, function (error) {
  return Promise.reject(error);
});

/** Persist new JWT everywhere it's stored. */
function saveToken(jwt: string) {
  localStorage.setItem('jhi-authenticationToken', jwt);
  sessionStorage.removeItem('jhi-authenticationToken');
  Preferences.set({ key: 'jwt', value: jwt });
}

function clearSessionAndRedirectToLogin(sessionExpired = true) {
  void clearClientSessionAndRedirectGuest(sessionExpired);
}

/** Single in-flight refresh promise to avoid multiple refresh calls. */
let refreshPromise: Promise<string | null> | null = null;

function doRefreshToken(currentToken: string | null): Promise<string | null> {
  if (!currentToken || !currentToken.trim()) {
    return Promise.resolve(null);
  }
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = axios
    .post<{ id_token: string }>(
      'api/refresh-token',
      {},
      {
        baseURL: axiosBaseURL(),
        headers: { Authorization: `Bearer ${currentToken}` },
      }
    )
    .then((res) => {
      const newJwt = res.data?.id_token ?? res.headers?.authorization?.replace(/^Bearer\s+/i, '') ?? null;
      if (newJwt) {
        saveToken(newJwt);
        return newJwt;
      }
      return null;
    })
    .catch(() => null)
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

// Add a response interceptor
axios.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error: AxiosError) {
    const status = error.response?.status;
    const config = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const requestUrl = config?.url ?? '';

    if (status !== 401 || !requestUrl || SKIP_401_URLS.test(requestUrl)) {
      return Promise.reject(error);
    }

    // Already retried once — give up and logout
    if (config._retry) {
      clearSessionAndRedirectToLogin(true);
      return Promise.reject(error);
    }

    const currentToken =
      (config.headers?.Authorization as string)?.replace(/^Bearer\s+/i, '') ||
      localStorage.getItem('jhi-authenticationToken') ||
      sessionStorage.getItem('jhi-authenticationToken');

    const newToken = await doRefreshToken(currentToken);

    if (newToken) {
      config._retry = true;
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${newToken}`;
      return axios.request(config);
    }

    clearSessionAndRedirectToLogin(true);
    return Promise.reject(error);
  }
);

